import { supabase } from '@/lib/supabase';

export interface OutfitItem {
  id: string;
  outfit_id: string;
  product_id: number | null;
  title: string;
  price: number | null;
  brand: string;
  image_url: string | null;
  link: string | null;
  position: number;
}

export interface Outfit {
  id: string;
  user_id: string;
  user_name: string;
  user_avatar: string | null;
  title: string;
  description: string | null;
  image_url: string | null;
  thumbnail_url: string | null;
  tags: string[];
  is_public: boolean;
  view_count: number;
  like_count: number;
  created_at: string;
  updated_at: string;
  items?: OutfitItem[];
  user_email?: string;
}

export const getOutfits = async (page = 1, perPage = 20): Promise<{ data: Outfit[]; count: number }> => {
  const start = (page - 1) * perPage;
  
  const { data, error, count } = await (supabase as any)
    .from('outfits')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(start, start + perPage - 1);

  if (error) throw error;
  
  // Fetch user emails for outfits
  const outfits = (data || []) as Outfit[];
  if (outfits.length > 0) {
    const userIds = [...new Set(outfits.map(o => o.user_id))];
    try {
      const { data: users } = await supabase.auth.admin.listUsers();
      if (users?.users) {
        const emailMap = new Map<string, string | undefined>(users.users.map(u => [u.id, u.email]));
        outfits.forEach(o => {
          o.user_email = (emailMap.get(o.user_id) || o.user_name) as string;
        });
      }
    } catch {
      // Admin API not available, just use user_name
    }
  }
  
  return { data: outfits, count: count || 0 };
};

export const getOutfitById = async (id: string): Promise<Outfit> => {
  const { data, error } = await (supabase as any)
    .from('outfits')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  const outfit = data as Outfit;

  // Fetch items
  const { data: items, error: itemsError } = await (supabase as any)
    .from('outfit_items')
    .select('*')
    .eq('outfit_id', id)
    .order('position', { ascending: true });

  if (!itemsError) {
    outfit.items = (items || []) as OutfitItem[];
  }

  return outfit;
};

export const deleteOutfit = async (id: string): Promise<void> => {
  // Get outfit to find image URL
  const { data: outfit } = await (supabase as any)
    .from('outfits')
    .select('image_url')
    .eq('id', id)
    .single();

  // Delete the outfit (cascade will delete items and likes)
  const { error } = await (supabase as any).from('outfits').delete().eq('id', id);
  if (error) throw error;

  // Delete image from storage if exists
  if (outfit?.image_url) {
    try {
      const path = outfit.image_url.split('/').pop();
      if (path) {
        await (supabase as any).storage.from('outfits').remove([path]);
      }
    } catch {
      // Ignore storage deletion errors
    }
  }
};

export const toggleOutfitVisibility = async (id: string, isPublic: boolean): Promise<void> => {
  const { error } = await (supabase as any)
    .from('outfits')
    .update({ is_public: isPublic })
    .eq('id', id);
  
  if (error) throw error;
};

export const searchOutfits = async (query: string): Promise<Outfit[]> => {
  const { data, error } = await (supabase as any)
    .from('outfits')
    .select('*')
    .or(`title.ilike.%${query}%,description.ilike.%${query}%,user_name.ilike.%${query}%`)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data || []) as Outfit[];
};

export const getOutfitStats = async (): Promise<{
  totalOutfits: number;
  totalLikes: number;
  totalViews: number;
  publicOutfits: number;
}> => {
  const { count: totalOutfits } = await (supabase as any)
    .from('outfits')
    .select('*', { count: 'exact', head: true });

  const { data: publicData } = await (supabase as any)
    .from('outfits')
    .select('id', { count: 'exact' })
    .eq('is_public', true);

  const { data: likesData } = await (supabase as any)
    .from('outfit_likes')
    .select('id', { count: 'exact', head: true });

  const { data: viewsData } = await (supabase as any)
    .from('outfits')
    .select('view_count');

  const totalViews = (viewsData || []).reduce((sum, o: any) => sum + (o.view_count || 0), 0);

  return {
    totalOutfits: totalOutfits || 0,
    totalLikes: (likesData as any)?.length || 0,
    totalViews,
    publicOutfits: (publicData as any)?.length || 0,
  };
};
